// src/components/surveys/questions/RatingScaleQuestion.tsx
import React from 'react';
import { SurveyQuestion } from '../../../types/survey.types';
import { Star } from 'lucide-react';

interface RatingScaleQuestionProps {
  question: SurveyQuestion;
  value?: number | null;
  onChange?: (value: number) => void;
  disabled?: boolean;
  showValidation?: boolean;
  error?: string;
  mode?: 'edit' | 'preview' | 'response';
}

const RatingScaleQuestion: React.FC<RatingScaleQuestionProps> = ({
  question,
  value,
  onChange,
  disabled = false,
  showValidation = false,
  error,
  mode = 'response',
}) => {
  const config = question.ratingConfig || { min: 1, max: 5 };
  const { min, max, minLabel, maxLabel, style } = config;

  const ratings = Array.from({ length: max - min + 1 }, (_, i) => min + i);

  const handleSelect = (rating: number) => {
    if (!disabled && onChange) {
      onChange(rating);
    }
  };

  // Star rating style
  if (style === 'stars') {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-1">
          {ratings.map((rating) => (
            <button
              key={rating}
              type="button"
              onClick={() => handleSelect(rating)}
              disabled={disabled || mode === 'edit'}
              className={`p-1 transition-transform ${
                disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:scale-110'
              }`}
            >
              <Star
                className={`w-8 h-8 ${
                  value !== null && value !== undefined && rating <= value
                    ? 'fill-yellow-400 text-yellow-400'
                    : 'text-gray-300'
                }`}
              />
            </button>
          ))}
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>{minLabel || min}</span>
          <span>{maxLabel || max}</span>
        </div>
        {showValidation && error && (
          <p className="text-sm text-red-600">{error}</p>
        )}
      </div>
    );
  }

  // Emoji style
  if (style === 'emoji') {
    const emojis = ['😡', '😕', '😐', '🙂', '😊'];
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          {ratings.map((rating, index) => {
            const emojiIndex = Math.floor((index / (ratings.length - 1)) * (emojis.length - 1));
            return (
              <button
                key={rating}
                type="button"
                onClick={() => handleSelect(rating)}
                disabled={disabled || mode === 'edit'}
                className={`p-2 text-3xl transition-transform rounded-lg ${
                  value === rating
                    ? 'bg-indigo-100 scale-110'
                    : 'hover:bg-gray-100 hover:scale-110'
                } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
              >
                {emojis[emojiIndex]}
              </button>
            );
          })}
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>{minLabel || 'Poor'}</span>
          <span>{maxLabel || 'Excellent'}</span>
        </div>
        {showValidation && error && (
          <p className="text-sm text-red-600">{error}</p>
        )}
      </div>
    );
  }

  // Default number scale style
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        {ratings.map((rating) => (
          <button
            key={rating}
            type="button"
            onClick={() => handleSelect(rating)}
            disabled={disabled || mode === 'edit'}
            className={`flex-1 min-w-[40px] py-3 border-2 rounded-lg font-medium transition-all ${
              value === rating
                ? 'border-indigo-500 bg-indigo-100 text-indigo-700'
                : 'border-gray-200 bg-white text-gray-600 hover:border-indigo-300 hover:bg-indigo-50'
            } ${disabled || mode === 'edit' ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'}`}
          >
            {rating}
          </button>
        ))}
      </div>
      <div className="flex justify-between text-xs text-gray-500">
        <span>{minLabel || `${min} - Low`}</span>
        <span>{maxLabel || `${max} - High`}</span>
      </div>
      {showValidation && error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default RatingScaleQuestion;
